/**
 * Display text helpers: strip "NULL", collapse spaces, avoid duplicate room in location.
 */

/**
 * Strip null/undefined/"NULL"/"undefined" and collapse whitespace.
 * @param {*} val - Any value (string, number, null, undefined)
 * @returns {string} Trimmed string, or '' for empty/null/NULL
 */
export function cleanText(val) {
  if (val == null || val === '') return '';
  let s = String(val).trim();
  if (/^null$/i.test(s) || /^undefined$/i.test(s)) return '';
  s = s.replace(/\bNULL\b/gi, '').replace(/\s+/g, ' ').trim();
  return s;
}

/**
 * Whole-value placeholders that mean "no real data" on imported records.
 * Matched case-insensitively against the fully-trimmed value only, so a value
 * that merely CONTAINS one of these (e.g. a model "NA-2000" or a room "NONE-1")
 * is preserved.
 */
const EMPTY_PLACEHOLDER_VALUES = new Set([
  'NULL', 'UNDEFINED', 'N/A', 'NA', 'N.A.', 'NIL', 'NONE', 'UNKNOWN', '-', '--', 'TBA', 'TBC',
]);

/**
 * Strict cleaner for building/room, headers, and make/model: filter out database
 * noise and common "no data" placeholders. Use before joining/displaying so
 * "B01" + "NULL" shows as "B01", and an imported model of "N/a" shows as blank
 * rather than "EATON / N/a".
 * @param {*} val - Any value
 * @returns {string} Trimmed string, or '' for empty/null/placeholder values
 */
export function cleanVal(val) {
  if (val == null || val === '') return '';
  const s = String(val).trim();
  return EMPTY_PLACEHOLDER_VALUES.has(s.toUpperCase()) ? '' : s;
}

/**
 * Single location line from location_description + room, without repeating the room number.
 * e.g. "BOOH R0001" + "R0001" → "BOOH R0001" (not "BOOH R0001 R0001").
 * @deprecated Prefer buildingRoomDisplay(building, room) for display; Building + Room are the source of truth.
 */
export function cleanLocation(locationDescription, room) {
  const loc = cleanText(locationDescription);
  const rm = room != null ? cleanText(room) : '';
  if (!loc && !rm) return '-';
  if (!rm) return loc;
  if (!loc) return rm;
  const locTrim = loc.trim();
  const roomTrim = rm.trim();
  if (locTrim.endsWith(roomTrim) || locTrim.includes(roomTrim)) return locTrim;
  return `${locTrim} ${roomTrim}`.trim();
}

/**
 * Display location from Building and Room only (no location_description).
 * Dedupes so "B00H - B00H" becomes "B00H".
 * @param {string} building - Building ID or name
 * @param {string} room - Room ID or name
 * @returns {string} Combined display or '-' if both empty
 */
export function buildingRoomDisplay(building, room) {
  const parts = [building, room].map(cleanVal).filter(Boolean);
  const deduped = [...new Set(parts)];
  return deduped.length ? deduped.join(' - ') : '-';
}

/**
 * Board number used to group distribution boards and their RCD circuits.
 * Prefers the structured db_number field; falls back to parsing the legacy
 * "<board> - <circuit>" equipment_code (space-hyphen first, then bare hyphen)
 * for assets not yet migrated. Returns '' when nothing usable is present.
 *
 * Shared so grouping code has a single source of truth for the board key.
 * @param {Object} asset
 * @returns {string}
 */
export function getDbNumberForGrouping(asset) {
  if (!asset || typeof asset !== 'object') return '';
  const dbNumber = cleanVal(asset.db_number);
  if (dbNumber) return dbNumber;
  const code = (asset.equipment_code || asset.asset_code || '').trim();
  if (!code) return '';
  // A distribution board's code IS the board number — never split it (board names
  // can contain hyphens, e.g. "DB-SOLO-77"). Only RCD codes carry a circuit suffix.
  if ((asset.asset_type || '').toLowerCase() === 'distribution_board') return code;
  if (code.includes(' - ')) return code.split(' - ')[0].trim() || code;
  if (code.includes('-')) return code.split('-')[0].trim() || code;
  return code;
}

/**
 * Canonical asset display label:
 * db_number(+circuit) -> equipment_code -> asset_code -> Building/Room -> #id.
 * Structured board/circuit fields take priority over the legacy string when
 * present; falls back to equipment_code for assets not yet migrated.
 * Keeps identity display consistent across assets, tests, and reports.
 * @param {Object} asset
 * @returns {string}
 */
export function assetDisplayLabel(asset) {
  if (!asset || typeof asset !== 'object') return '-';

  const dbNumber = cleanText(asset.db_number);
  const circuit = cleanText(asset.circuit_breaker_number);
  if (dbNumber && circuit) return `${dbNumber} - ${circuit}`;
  if (dbNumber) return dbNumber;

  const equipmentCode = cleanText(asset.equipment_code);
  if (equipmentCode) return equipmentCode;

  const assetCode = cleanText(asset.asset_code ?? asset.assetCode);
  if (assetCode) return assetCode;

  const location = buildingRoomDisplay(asset.building, asset.room);
  if (location !== '-') return location;

  const id = asset.id ?? asset.asset_id ?? asset.assetId;
  return id != null ? `#${id}` : '-';
}
